import { Box, Button, Select, SvgIcon } from '@volue/wave-react';
import { ascend, head, sort } from 'ramda';
import React, { useState } from 'react';

import type {
  TacticId,
  CalculationSets
} from '@/overmind/effects/api/orchestratorApi';
import { InlineNotification } from '@/features/execute-calculation-set/components/Calculation/InlineNotification'

type ChooseCalculationSetProps = {
  runCalculationSet: (tactic: TacticId) => void;
  calculationSets: CalculationSets;
  isCalculationSetRunning: boolean;
  calculationTacticExecutionStatusText: any;
  shouldDisplaySessionExpiredError: boolean;
  restartApp: () => void;
};

function mapToKeyValuePair(calculationSets: CalculationSets) {
  return Object.entries(calculationSets).map(([key, tactic]) => {
    return {
      value: key,
      label: tactic.name,
      sequenceNumber: tactic.sequenceNumber
    };
  });
}

export function ChooseCalculationSet(props: ChooseCalculationSetProps) {
  const unorderedCalculationSets = mapToKeyValuePair(props.calculationSets);
  const availableCalculationSets = sort(
    ascend(tactic => tactic.sequenceNumber),
    unorderedCalculationSets
  );
  const [selectedTactic, setSelectedTactic] = useState<TacticId>(
    (
      head(
        availableCalculationSets
      ) as unknown as typeof availableCalculationSets[number]
    ).value
  );
  const sortedTacticOptions = availableCalculationSets.map(calculationSet => ({
    value: calculationSet.value,
    label: calculationSet.label
  }));
  const isCalculationSetRunning = props.isCalculationSetRunning;
  if (props.shouldDisplaySessionExpiredError) {
    return (
      <InlineNotification
        type="ERROR"
        content="Your session has expired."
        actionButtons={[
          <Button className="mRn" onClick={props.restartApp}>
            {'Restart application'}
          </Button>
        ]}
      />
    );
  }
  return (
    <>
      <Box css={{ color: '$colorGray50' }}>{'Choose calculation set'}</Box>
      <Select
        data-testid="calculation-sets-dropdown"
        isDisabled={isCalculationSetRunning}
        value={selectedTactic}
        onChange={event => setSelectedTactic(event as TacticId)}
        aria-label="Input label"
        options={sortedTacticOptions}
      />
      <RunCalculationSetButton
        calculationTacticExecutionStatusText={
          props.calculationTacticExecutionStatusText
        }
        onClick={() => props.runCalculationSet(selectedTactic)}
        isCalculationSetRunning={isCalculationSetRunning}
      />
    </>
  );
}

type RunCalculationSetButtonProps = {
  onClick: () => void;
  isCalculationSetRunning: boolean;
  calculationTacticExecutionStatusText: any;
};
const RunCalculationSetButton: React.FunctionComponent<
  RunCalculationSetButtonProps
> = ({
  onClick,
  isCalculationSetRunning,
  calculationTacticExecutionStatusText
}) => {
  return (
    <Button
      data-testid="run-calculation-set-btn"
      leftIcon={<SvgIcon iconName="play" />}
      isLoading={isCalculationSetRunning}
      loadingText={
        calculationTacticExecutionStatusText.type === 'NO_TEXT'
          ? 'Not loading'
          : calculationTacticExecutionStatusText.text
      }
      onClick={onClick}
    >
      {'Run calculation set'}
    </Button>
  );
};
